import { useI18n } from '@react/i18n';
import { LookupData, UserInput } from '../types';
import { UpdateUserInput } from '../VratkyApp';

/**
 * Číslo účtu (CZ) / IBAN (ostatní) + checkbox „Bez čísla účtu" + speciální
 * podmínky – sdílené mezi formulářem vratky a reklamace.
 * U plateb převodem/dobírkou je účet povinný vždy (peníze se musí kam vracet).
 */

export function requiresAccountNumber(data: LookupData): boolean {
    return data.objednavka.forma_uhrady === 'Převodním příkazem'
        || data.objednavka.forma_uhrady === 'Dobírkou';
}

export function prefillBankAccount(data: LookupData, userInput: UserInput, update: UpdateUserInput): void {
    const o = data.objednavka;
    if (o.shop_country_code === 'cz' && o.cislo_uctu && o.kod_banky && !userInput.cislo_uctu_user) {
        update('cislo_uctu_user', `${o.cislo_uctu}/${o.kod_banky}`);
    }
    if (o.shop_country_code !== 'cz' && !userInput.iban_user && o.iban) {
        update('iban_user', o.iban);
    }
}

interface Props {
    userInput: UserInput;
    update: UpdateUserInput;
    data: LookupData;
}

export function BankAccountSection({ userInput, update, data }: Props) {
    const t = useI18n();
    const required = requiresAccountNumber(data);
    const isCz = data.objednavka.shop_country_code === 'cz';
    const allowNoAccount = !required && data.objednavka.dni_od_data_vystaveni_faktury < 150;

    return (
        <>
            <div className="vr-field">
                {isCz ? (
                    <>
                        <label>{t('Číslo účtu pro případné vrácení peněz')}<span className="vr-required">*</span></label>
                        <input
                            type="text"
                            className="form-control"
                            disabled={userInput.no_account_number}
                            value={userInput.cislo_uctu_user}
                            onChange={(e) => update('cislo_uctu_user', e.target.value)}
                            placeholder="xxxxxx/xxxx"
                        />
                    </>
                ) : (
                    <>
                        <label>{t('IBAN číslo účtu pro případné vrácení peněz')}<span className="vr-required">*</span></label>
                        <input
                            type="text"
                            className="form-control"
                            disabled={userInput.no_account_number}
                            value={userInput.iban_user}
                            onChange={(e) => update('iban_user', e.target.value)}
                            placeholder="XX000000000"
                        />
                    </>
                )}
                {allowNoAccount && (
                    <label className="vr-check">
                        <input
                            type="checkbox"
                            checked={userInput.no_account_number}
                            onChange={(e) => update('no_account_number', e.target.checked)}
                        />
                        {t('Bez čísla účtu')}
                    </label>
                )}
            </div>

            <div className="vr-field">
                <label className="vr-check">
                    <input
                        type="checkbox"
                        checked={userInput.has_special_conditions}
                        onChange={(e) => update('has_special_conditions', e.target.checked)}
                    />
                    {t('Speciální podmínky domluvené se zástupcem Bola')}
                </label>
            </div>

            {userInput.has_special_conditions && (
                <div className="vr-field">
                    <label>{t('Uveďte jméno zástupce a domluvené podmínky')}<span className="vr-required">*</span></label>
                    <textarea
                        maxLength={500}
                        className="form-control"
                        rows={2}
                        placeholder="..."
                        value={userInput.special_conditions}
                        onChange={(e) => update('special_conditions', e.target.value)}
                    />
                </div>
            )}
        </>
    );
}

export type FormError =
    | 'duvod_missing'
    | 'special_conditions_missing'
    | 'ucet_missing'
    | 'iban_missing'
    | 'wrong_account_number'
    | 'wrong_iban';

export function FormErrorAlert({ error, userInput }: { error: FormError | null; userInput: UserInput }) {
    const t = useI18n();
    if (!error) {
        return null;
    }

    return (
        <>
            {error === 'special_conditions_missing' && userInput.has_special_conditions && !userInput.special_conditions && (
                <div className="vr-alert vr-alert--danger" role="alert">{t('Prosím uveďte jméno zástupce a domluvené podmínky.')}</div>
            )}
            {error === 'ucet_missing' && !userInput.cislo_uctu_user && (
                <div className="vr-alert vr-alert--danger" role="alert">{t('Prosím zadejte číslo účtu.')}</div>
            )}
            {error === 'iban_missing' && !userInput.iban_user && (
                <div className="vr-alert vr-alert--danger" role="alert">{t('Prosím zadejte IBAN číslo účtu.')}</div>
            )}
            {error === 'wrong_account_number' && !userInput.no_account_number && (
                <div className="vr-alert vr-alert--danger" role="alert">{t('Nespravné číslo účtu. Zadejte jej prosím ve formátu xx-xxxxxxxxx/xxxx a zkontrolujte na překlepy.')}</div>
            )}
            {error === 'wrong_iban' && !userInput.no_account_number && (
                <div className="vr-alert vr-alert--danger" role="alert">{t('Nespravný IBAN. Zadejte jej prosím ve formátu XX1111111111 a zkontrolujte na překlepy.')}</div>
            )}
        </>
    );
}
